import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CalendarPlus } from "lucide-react";
import { TIPOS_MODALIDAD, VEHICULOS, IDIOMAS } from "@/lib/constants";

export default function ProgramarSalidaDialog({ open, onOpenChange, fecha, tours, onCreated }) {
  const [tourId, setTourId] = useState("");
  const [hora, setHora] = useState("");
  const [modalidad, setModalidad] = useState("regular");
  const [vehiculo, setVehiculo] = useState("bus");
  const [idioma, setIdioma] = useState("es");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTourId("");
      setHora("");
      setModalidad("regular");
      setVehiculo("bus");
      setIdioma("es");
    }
  }, [open]);

  const crear = async () => {
    if (!tourId || !hora) return toast.error("Elegí el tour y el horario.");
    setSaving(true);
    try {
      const { data } = await api.post("/salidas", {
        tour_id: tourId,
        fecha,
        hora,
        modalidad,
        vehiculo,
        idioma,
      });
      toast.success("Salida programada. Ahora podés cargarle los vehículos del día.");
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="programar-salida-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CalendarPlus className="h-5 w-5" /> Programar salida
          </DialogTitle>
          <DialogDescription>
            Creá una salida de antemano para el {fecha}, antes de que lleguen reservas, y configurale
            los vehículos del día.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Tour</Label>
            <Select value={tourId} onValueChange={setTourId}>
              <SelectTrigger data-testid="programar-tour-select">
                <SelectValue placeholder="Elegí un tour" />
              </SelectTrigger>
              <SelectContent>
                {tours.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Hora</Label>
              <Input type="time" value={hora} onChange={(e) => setHora(e.target.value)} data-testid="programar-hora-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Modalidad</Label>
              <Select value={modalidad} onValueChange={setModalidad}>
                <SelectTrigger data-testid="programar-modalidad-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TIPOS_MODALIDAD).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Vehículo principal</Label>
              <Select value={vehiculo} onValueChange={setVehiculo}>
                <SelectTrigger data-testid="programar-vehiculo-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(VEHICULOS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Idioma</Label>
              <Select value={idioma} onValueChange={setIdioma}>
                <SelectTrigger data-testid="programar-idioma-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(IDIOMAS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={crear} disabled={saving} data-testid="programar-salida-confirm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Programar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
